"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { ThermometerSnowflake, Zap, Droplets, Factory, Gauge, CircleCheck as CheckCircle2, ArrowRight, Cpu, Flame, Wind, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
const stats = [{
  id: "stat-1",
  value: "100%",
  label: "Evaporação completa",
  detail: "Sem acumulação de líquido nas condutas ou precipitadores."
}, {
  id: "stat-2",
  value: "-45%",
  label: "Consumo de energia",
  detail: "Redução no consumo de compressores e ventiladores de tiragem."
}, {
  id: "stat-3",
  value: "±1°C",
  label: "Precisão térmica",
  detail: "Controlo contínuo e estável da temperatura de saída dos gases."
}, {
  id: "stat-4",
  value: "30+ Anos",
  label: "Experiência de engenharia",
  detail: "Parceiro oficial da Spraying Systems Co. em Portugal desde 1993."
}];
const pillars = [{
  id: "pillar-1",
  title: "Atomização ultra-fina (D32 reduzido)",
  description: "A taxa de evaporação depende da área de superfície do líquido. Gotas atomizadas em micro-escala evaporam em fracções de segundo antes de contactarem as paredes.",
  icon: Droplets,
  badge: "Termodinâmica"
}, {
  id: "pillar-2",
  title: "Modulação de fluxo em circuito fechado",
  description: "Ajuste em tempo real do caudal de injeção face às oscilações de carga térmica, mantendo a humidade relativa e o ponto de orvalho dentro dos limites de segurança.",
  icon: Gauge,
  badge: "Controlo Preciso"
}, {
  id: "pillar-3",
  title: "Eficiência energética e baixo consumo",
  description: "Aproveitamento do calor latente de vaporização da água, eliminando a dependência de potentes grupos de frio e sistemas mecânicos dispendiosos.",
  icon: Zap,
  badge: "Sustentabilidade"
}];
const applicationTabs = [{
  id: "gases",
  name: "Arrefecimento de Gases",
  icon: Flame,
  title: "Condicionamento e arrefecimento de gases de combustão",
  description: "Proteja filtros de mangas, precipitadores eletrostáticos (ESP) e ventiladores a jusante através da descida controlada da temperatura dos fumos industriais.",
  benefits: ["Evita a queima ou degradação prematura das mangas filtrantes", "Reduz o volume total de gás, diminuindo a potência exigida aos exaustores", "Garante a ausência de lama nas paredes dos ductos de exaustão"],
  sectors: "Cimenteiras, Incineradoras, Siderurgia e Vidro",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/gas_cooling_system.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/gas_cooling_system.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/gas_cooling_system.png-wvc-sizes",
  imgAlt: "Sistema de arrefecimento evaporativo de gases de combustão"
}, {
  id: "datacenters",
  name: "Data Centers & IT",
  icon: Cpu,
  title: "Arrefecimento evaporativo direto e indireto para centros de dados",
  description: "Aumento drástico do PUE (Power Usage Effectiveness) com pulverização de névoa de alta pressão em trocadores de calor e baterias de condensação.",
  benefits: ["Redução acentuada do consumo elétrico em picos de verão", "Geração de microgotas sem molhagem indesejada de condutas elétricas", "Funcionamento fiável com bicos antientupimento em aço inoxidável"],
  sectors: "Centros de Dados, Telecomunicações e Salas Elétricas",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/datacenter_cooling_nozzles.webp",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/datacenter_cooling_nozzles.webp-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/datacenter_cooling_nozzles.webp-wvc-sizes",
  imgAlt: "Tecnologia de arrefecimento evaporativo para salas de servidores"
}, {
  id: "metalurgia",
  name: "Siderurgia & Metais",
  icon: Factory,
  title: "Têmpera e arrefecimento contínuo de perfis e lingotes",
  description: "Distribuição uniforme de caudal e padrão de impacto térmico controlado para evitar empenos, tensões mecânicas e descontinuidades metalúrgicas.",
  benefits: ["Padrões de spray planos ou cone cheio calibrados ao milímetro", "Lanças de injeção retráteis e resistentes a atmosferas agressivas", "Controlo multizona adaptável à velocidade do laminador"],
  sectors: "Siderurgia, Fundições e Extrusão de Alumínio",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/steel_evaporative_cooling.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/steel_evaporative_cooling.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/steel_evaporative_cooling.png-wvc-sizes",
  imgAlt: "Processo de arrefecimento industrial na indústria do aço"
}, {
  id: "ambientes",
  name: "Climatização Fabril",
  icon: Wind,
  title: "Abatimento de temperatura e controlo ambiental em naves fabris",
  description: "Arrefecimento adiabático de grandes volumes e armazéns com baixo consumo hídrico e elétrico, melhorando o conforto e a estabilidade dos produtos.",
  benefits: ["Descida de até 10°C na temperatura ambiente de trabalho", "Controlo conjunto de poeiras em suspensão e eletricidade estática", "Integração rápida em linhas existentes com manutenção simplificada"],
  sectors: "Indústria da Madeira, Cortiça, Têxtil e Logística",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/factory_evaporative_climate.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/factory_evaporative_climate.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/factory_evaporative_climate.png-wvc-sizes",
  imgAlt: "Nave industrial climatizada com pulverização evaporativa"
}];
const technicalSteps = [{
  step: "01",
  title: "Modelação CFD e Dimensionamento",
  description: "Simulação de dinâmica de fluidos para prever trajetórias de gotas, tempos de residência e perfis térmicos reais na conduta."
}, {
  step: "02",
  title: "Seleção do Bico e Lança Injetora",
  description: "Escolha entre atomização hidráulica ou ar comprimido (FloMax® / AutoJet®) com materiais resistentes a abrasão e corrosão."
}, {
  step: "03",
  title: "Integração de Controlo Automático",
  description: "Painéis AutoJet® integrados com PLC de fábrica para modulação rápida e proteção em caso de desvios térmicos."
}];
export default function EvaporativeCooling() {
  const [activeTab, setActiveTab] = useState("gases");
  return <section id="evaporativecoolingtechnology" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <ThermometerSnowflake className="size-3.5" />
            Engenharia Térmica e de Processos
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default">
            Arrefecimento evaporativo industrial com máxima eficiência
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Reduza temperaturas operacionais utilizando a física da evaporação espontânea. Os
            sistemas Eurocontrol com tecnologia Spraying Systems Co. convertem calor sensível em calor
            latente com consumo energético mínimo e zero acumulação de água.
          </p>
        </div>

        {/* 3 Core Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.id} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="p-2.5 rounded-lg bg-accent text-accent-foreground">
                        <Icon className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {pillar.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg sm:text-xl font-semibold text-card-foreground">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Interactive Applications Tabs */}
        <div className="mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Aplicações Práticas
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground mt-1">
                Soluções configuradas para o seu setor
              </h3>
            </div>
            <Link to="/ApplicationsPage">
              <Button variant="outline" size="sm" className="cursor-pointer gap-2">
                Ver todas as aplicações
                <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
            <TabsList className="w-full flex flex-wrap md:inline-flex justify-start h-auto p-1.5 bg-muted/80 gap-1 rounded-xl">
              {applicationTabs.map((tab, i) => {
              const TabIcon = tab.icon;
              return <TabsTrigger key={tab.id} value={tab.id} data-index={i} className="flex items-center gap-2 py-2 px-3 sm:px-4 text-xs sm:text-sm font-medium rounded-lg cursor-pointer transition-colors data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                    <TabIcon className="size-4" />
                    <span>{tab.name}</span>
                  </TabsTrigger>;
            })}
            </TabsList>

            {applicationTabs.map((tab, i) => <TabsContent key={tab.id} value={tab.id} data-index={i} className="mt-0">
                <Card className="border border-border bg-card shadow-sm overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 sm:p-8 items-center">
                    <div className="lg:col-span-7 flex flex-col justify-center space-y-5">
                      <div className="space-y-2">
                        <Badge variant="secondary" className="text-xs font-medium">
                          {tab.sectors}
                        </Badge>
                        <h4 className="text-xl sm:text-2xl font-bold text-card-foreground">
                          {tab.title}
                        </h4>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {tab.description}
                        </p>
                      </div>

                      <div className="space-y-2.5 pt-2 border-t border-border">
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                          Vantagens operacionais diretas:
                        </span>
                        <ul className="space-y-2">
                          {tab.benefits.map((benefit, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                              <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                              <span>{benefit}</span>
                            </li>)}
                        </ul>
                      </div>

                      <div className="pt-2 flex flex-wrap items-center gap-3">
                        <Link to="/ContactsPage">
                          <Button size="default" className="cursor-pointer">
                            Solicitar cálculo térmico
                          </Button>
                        </Link>
                        <Link to="/TechnicalSolutionsPage">
                          <Button variant="ghost" size="default" className="cursor-pointer gap-1.5">
                            Ficha técnica e lanças
                            <ArrowRight className="size-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>

                    <div className="lg:col-span-5">
                      <div className="relative rounded-xl overflow-hidden border border-border bg-muted aspect-4/3 shadow-inner">
                        <img src={tab.imgSrc} data-wvc-srcset={tab.imgSrcset} data-wvc-sizes={tab.imgSizes} alt={tab.imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent opacity-60 pointer-events-none" />
                        <div className="absolute bottom-3 left-3 right-3 p-3 rounded-lg bg-card/90 backdrop-blur-xs border border-border">
                          <div className="flex items-center gap-2">
                            <Layers className="size-4 text-secondary" />
                            <span className="text-xs font-medium text-foreground">
                              Modelado por dinâmica de fluidos (CFD)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Process Roadmap / Engineering Rigor */}
        <div className="mt-20 pt-16 border-t border-border">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="outline" className="mb-2">Metodologia Eurocontrol</Badge>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Como garantimos evaporação total sem depósitos
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              O sucesso do arrefecimento por pulverização depende do balanço rigoroso entre tempo de residência dos gases e tamanho das gotas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {technicalSteps.map((item, i) => <div key={item.step} data-index={i} className="relative rounded-xl border border-border bg-card p-6 shadow-xs flex flex-col justify-between">
                <div>
                  <div className="text-3xl font-black text-secondary/30 mb-2 font-mono">
                    {item.step}
                  </div>
                  <h4 className="text-lg font-semibold text-card-foreground mb-2">
                    {item.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Performance & Trust Stats Banner */}
        <div className="mt-16 rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 shadow-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 divide-y sm:divide-y-0 sm:divide-x divide-primary-foreground/15">
            {stats.map((stat, i) => <div key={stat.id} data-index={i} className={`pt-6 sm:pt-0 ${i !== 0 ? "sm:pl-6 lg:pl-8" : ""}`}>
                <div className="text-3xl sm:text-4xl font-extrabold tracking-tight text-primary-foreground">
                  {stat.value}
                </div>
                <div className="text-sm font-semibold text-primary-foreground/90 mt-1">
                  {stat.label}
                </div>
                <p className="text-xs text-primary-foreground/75 mt-1.5 leading-relaxed">
                  {stat.detail}
                </p>
              </div>)}
          </div>
        </div>

        {/* Bottom CTA Strip */}
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 p-6 rounded-xl border border-border bg-muted/40">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-base font-semibold text-foreground">
              Precisa de analisar a sua conduta ou torre de arrefecimento?
            </h4>
            <p className="text-sm text-muted-foreground">
              Os nossos engenheiros calculam o caudal ideal e o tamanho de gota para a sua instalação.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/ContactsPage">
              <Button size="default" className="cursor-pointer">
                Consulte-nos
              </Button>
            </Link>
            <Link to="/CatalogsPage">
              <Button variant="outline" size="default" className="cursor-pointer">
                Ver Catálogos
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}